/**
 * Fishing. Spots on the map belong to a kind of water (their `kind`). Each water holds
 * several fish, and each fish is caught one way. What you carry decides how you fish a
 * spot, and so what you catch: a net scoops minnows, a fly rod takes trout, a baited rod
 * takes perch or pike, a trap left in the fen fills with eels.
 *
 * Time of day and weather change what bites. Every fish rolls a weight; the journal keeps
 * your records and the world keeps its own.
 */
import type { ItemId } from './items.js';
import type { SupplyId, ToolLine } from './tools.js';
import type { HeldTool } from '../protocol.js';

export type WaterKind = 'shore' | 'river' | 'lake' | 'fen' | 'cove' | 'quarry' | 'tarn' | 'reservoir' | 'sump';
export type Method = 'net' | 'fly' | 'bait' | 'harpoon' | 'trap' | 'pot';
export type Weather = 'clear' | 'cloudy' | 'rain';

export interface WaterDef { name: string; blurb: string }
export const WATERS: Record<WaterKind, WaterDef> = {
  shore: { name: 'Sea shallows', blurb: 'Salt water off Saltreach.' },
  river: { name: 'River run', blurb: 'Quick water over stones.' },
  lake: { name: 'Lake water', blurb: 'Deep and still.' },
  fen: { name: 'Fen channel', blurb: 'Brown water among the reeds.' },
  cove: { name: 'Cove water', blurb: 'Clear and cold. Things glow down there.' },
  quarry: { name: 'Flooded quarry', blurb: 'Rust-red water in the Reavers\' old cut. Something lives in it anyway.' },
  tarn: { name: 'Mountain tarn', blurb: 'Snowmelt in a bowl of rock on Ashen Ridge. Cold enough to hurt.' },
  reservoir: { name: 'The reservoir', blurb: 'Still water behind a cracked dam. The Facility drew from it, once.' },
  sump: { name: 'The sump', blurb: 'Black water at the bottom of the Delve. Nothing down here has eyes.' },
};
export const WATER_KINDS = Object.keys(WATERS) as WaterKind[];
export const isWaterKind = (v: unknown): v is WaterKind => typeof v === 'string' && Object.prototype.hasOwnProperty.call(WATERS, v);

export interface MethodDef {
  line: ToolLine;
  uses?: SupplyId;
  /** "Net", "Fly-fish" … for tooltips: "Fly-fish for trout". */
  verb: string;
  doing: string;
  /** Ticks between attempts (active methods). */
  everyTicks: number;
  /** Traps and pots are set and left; you come back to haul them. */
  passive?: boolean;
  /** Can a fish "bite" (the optional strike)? */
  bites?: boolean;
  held: HeldTool;
}
export const METHODS: Record<Method, MethodDef> = {
  net: { line: 'net', verb: 'Net', doing: 'Netting', everyTicks: 3, held: 'net' },
  bait: { line: 'rod', uses: 'bait', verb: 'Bait-fish for', doing: 'Bait fishing for', everyTicks: 3, bites: true, held: 'rod' },
  fly: { line: 'flyrod', uses: 'feathers', verb: 'Fly-fish for', doing: 'Fly fishing for', everyTicks: 3, bites: true, held: 'rod' },
  harpoon: { line: 'harpoon', verb: 'Harpoon', doing: 'Harpooning', everyTicks: 5, bites: true, held: 'harpoon' },
  trap: { line: 'trap', uses: 'bait', verb: 'Set an eel trap for', doing: 'Setting a trap', everyTicks: 3, passive: true, held: 'trap' },
  pot: { line: 'pot', uses: 'bait', verb: 'Sink a crab pot for', doing: 'Sinking a pot', everyTicks: 3, passive: true, held: 'trap' },
};

export interface SpeciesDef {
  name: string;
  /** Plural or mass noun for messages: "minnows", "trout". */
  plural: string;
  raw: ItemId;
  level: number;
  xp: number;
  method: Method;
  waters: WaterKind[];
  /** Weight range in kg; most catches are near the bottom. */
  kg: [number, number];
  /** How common it is against the other fish caught the same way in the same water. */
  freq: number;
  /** 'night': only at night. 'twilight': bites best at dawn and dusk. 'nocturnal': bites best at night. */
  time?: 'night' | 'twilight' | 'nocturnal';
  /** Bites far better in the rain, poorly otherwise. */
  rain?: boolean;
  /** Needs at least this tier of its method's tool (a steel rod, a starsilver harpoon). */
  tier?: number;
}

export const SPECIES = {
  /* home band: the Meadow river, the Landing shore */
  minnow: { name: 'Minnow', plural: 'minnows', raw: 'raw_minnow', level: 1, xp: 10, method: 'net', waters: ['shore', 'river', 'lake'], kg: [0.02, 0.09], freq: 10 },
  perch: { name: 'Perch', plural: 'perch', raw: 'raw_perch', level: 5, xp: 16, method: 'bait', waters: ['lake', 'river', 'fen'], kg: [0.15, 1.6], freq: 9 },
  trout: { name: 'Trout', plural: 'trout', raw: 'raw_trout', level: 10, xp: 24, method: 'fly', waters: ['river'], kg: [0.4, 4], freq: 9, time: 'twilight' },
  sardine: { name: 'Sardine', plural: 'sardines', raw: 'raw_sardine', level: 15, xp: 26, method: 'net', waters: ['shore', 'cove'], kg: [0.05, 0.22], freq: 7 },
  /* middle band: Glimmer Lake, Brackmire Fen, Saltreach */
  carp: { name: 'Carp', plural: 'carp', raw: 'raw_carp', level: 20, xp: 38, method: 'bait', waters: ['fen', 'lake'], kg: [1, 14], freq: 7 },
  pike: { name: 'Pike', plural: 'pike', raw: 'raw_pike', level: 25, xp: 50, method: 'bait', waters: ['lake'], kg: [1.5, 16], freq: 5 },
  salmon: { name: 'Salmon', plural: 'salmon', raw: 'raw_salmon', level: 30, xp: 62, method: 'fly', waters: ['river'], kg: [2, 20], freq: 4, time: 'twilight' },
  eel: { name: 'Eel', plural: 'eels', raw: 'raw_eel', level: 35, xp: 72, method: 'trap', waters: ['fen', 'river'], kg: [0.5, 4.5], freq: 6, time: 'nocturnal' },
  crab: { name: 'Crab', plural: 'crabs', raw: 'raw_crab', level: 40, xp: 84, method: 'pot', waters: ['shore', 'cove'], kg: [0.4, 3], freq: 6 },
  /* outer band: Stillwater Cove, the flooded quarry at Rustcliff */
  glowfin: { name: 'Glowfin', plural: 'glowfin', raw: 'raw_glowfin', level: 45, xp: 120, method: 'harpoon', waters: ['cove', 'tarn'], kg: [2, 10], freq: 6, time: 'night' },
  rustgill: { name: 'Rustgill', plural: 'rustgill', raw: 'raw_rustgill', level: 50, xp: 110, method: 'bait', waters: ['quarry'], kg: [0.8, 7], freq: 6 },
  stormfin: { name: 'Stormfin', plural: 'stormfin', raw: 'raw_stormfin', level: 55, xp: 200, method: 'harpoon', waters: ['shore', 'cove'], kg: [8, 70], freq: 3, rain: true },
  lobster: { name: 'Lobster', plural: 'lobsters', raw: 'raw_lobster', level: 60, xp: 240, method: 'pot', waters: ['cove', 'shore'], kg: [0.6, 5], freq: 6, tier: 1 },
  sturgeon: { name: 'Sturgeon', plural: 'sturgeon', raw: 'raw_sturgeon', level: 65, xp: 140, method: 'bait', waters: ['lake', 'river'], kg: [6, 90], freq: 3, tier: 1 },
  /* frontier: the tarn on Ashen Ridge, the sea beyond Saltreach, the reservoir, the Delve's sump */
  char: { name: 'Ashen char', plural: 'char', raw: 'raw_char', level: 70, xp: 205, method: 'fly', waters: ['tarn'], kg: [0.8, 9], freq: 6 },
  longjaw: { name: 'Longjaw', plural: 'longjaw', raw: 'raw_longjaw', level: 75, xp: 300, method: 'harpoon', waters: ['cove', 'shore'], kg: [20, 160], freq: 2, tier: 2 },
  glassback: { name: 'Glassback', plural: 'glassback', raw: 'raw_glassback', level: 80, xp: 245, method: 'bait', waters: ['reservoir', 'lake'], kg: [1, 12], freq: 6 },
  cavefish: { name: 'Blind cavefish', plural: 'cavefish', raw: 'raw_cavefish', level: 85, xp: 700, method: 'trap', waters: ['sump', 'quarry'], kg: [0.3, 4], freq: 6 },
  deepmaw: { name: 'Deepmaw', plural: 'deepmaw', raw: 'raw_deepmaw', level: 90, xp: 850, method: 'harpoon', waters: ['reservoir', 'lake'], kg: [15, 120], freq: 4, time: 'night', tier: 3 },
  ghostgill: { name: 'Ghostgill', plural: 'ghostgill', raw: 'raw_ghostgill', level: 95, xp: 490, method: 'harpoon', waters: ['sump', 'reservoir'], kg: [5, 40], freq: 5, tier: 3 },
} satisfies Record<string, SpeciesDef>;
export type SpeciesId = keyof typeof SPECIES;
export const SPECIES_IDS = Object.keys(SPECIES) as SpeciesId[];
export const speciesDef = (id: SpeciesId): SpeciesDef => SPECIES[id] as SpeciesDef;
export const isSpeciesId = (v: unknown): v is SpeciesId => typeof v === 'string' && Object.prototype.hasOwnProperty.call(SPECIES, v);

/** One named giant per water. A rare roll on top of a catch of `of`, at that water. */
export interface LegendDef { name: string; item: ItemId; water: WaterKind; of: SpeciesId; kg: number; lore: string }
export const LEGENDS = {
  legend_pike: { name: 'Old Glimmerjaw', item: 'legend_pike', water: 'lake', of: 'pike', kg: 31.4, lore: 'The pike that ate the lake. Tamsin swears it took her grandfather\'s boot.' },
  legend_salmon: { name: 'The Silver Ghost', item: 'legend_salmon', water: 'river', of: 'salmon', kg: 34.2, lore: 'Seen at dawn, never twice in the same pool.' },
  legend_carp: { name: 'Mother Mudback', item: 'legend_carp', water: 'fen', of: 'carp', kg: 41.8, lore: 'Older than the fen bell, they say, and twice as heavy.' },
  legend_glowfin: { name: 'Moonfin', item: 'legend_glowfin', water: 'cove', of: 'glowfin', kg: 22.6, lore: 'It lights the whole cove when it turns.' },
  legend_stormfin: { name: 'The Saltreach King', item: 'legend_stormfin', water: 'shore', of: 'stormfin', kg: 148, lore: 'Comes in with the worst weather. Leaves with your harpoon, usually.' },
  legend_rustgill: { name: 'Old Rust', item: 'legend_rustgill', water: 'quarry', of: 'rustgill', kg: 19.5, lore: 'The Reavers feed it what they don\'t want found. It has grown fat on secrets.' },
  legend_deepmaw: { name: 'The Warden', item: 'legend_deepmaw', water: 'reservoir', of: 'deepmaw', kg: 214, lore: 'It patrols the dam like it was told to. Maybe it was.' },
} satisfies Record<string, LegendDef>;
export type LegendId = keyof typeof LEGENDS;
export const LEGEND_IDS = Object.keys(LEGENDS) as LegendId[];
/** Chance per catch of the legend's species at its water, once you are 5 levels past that fish. */
export const LEGEND_CHANCE = 1 / 1200;

/** Odd things that come up instead of a fish (active methods only). */
export const FIND_CHANCE = { junk: 0.02, bottle: 0.004 };
export const JUNK: ItemId[] = ['old_boot', 'driftwood'];

/** Spots drift to nearby water this often (ticks, randomised in the range). */
export const SPOT_MOVE_TICKS: [number, number] = [120, 260];
/** How far from where the map put it a spot can wander. */
export const SPOT_RANGE = 5;

/** Traps and pots: how long to fill, how many fish at most, how many you can have out. */
export const TRAP_FILL_SECONDS = 30;
export const TRAP_MAX_CATCH = 5;
export const maxTraps = (fishingLevel: number) => 2 + Math.floor(fishingLevel / 20);

/** Bite window for the optional strike, in ticks. */
export const BITE_TICKS = 3;
export const BITE_CHANCE = 0.14;

/** Day phases the fish care about. */
export const isTwilight = (t: number) => t < 0.1 || (t > 0.45 && t < 0.6);

/** "380 g" or "4.2 kg". */
export const fmtKg = (kg: number) => (kg < 1 ? `${Math.round(kg * 1000)} g` : `${kg >= 100 ? Math.round(kg) : kg.toFixed(1)} kg`);
